import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Divider from '@material-ui/core/Divider';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import LiveTvIcon from '@material-ui/icons/LiveTv';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';
import LanguageIcon from '@material-ui/icons/Language';
import BuildIcon from '@material-ui/icons/Build';
import Container from 'common/Container';
import PriceVatDisclaimer from 'common/PriceVatDisclaimer';
import TnoEcosystemSection from '../TNOMediaKitProposal/components/TnoEcosystemSection';
import { useTheme } from '@material-ui/core/styles';
import {
  mergeBrand,
  SectionHeader,
  ProgramCard,
  ProgramIndexNav,
  PlanHighlightStrip,
  ValuePillarCard,
  ExtraGroupCard,
  CtaBand,
} from './TnoConCommercialUi';
import HorizontalPlansSection from './HorizontalPlansSection';

const METRIC_ACCENTS = ['#c62828', '#00695c', '#00838f', '#5e35b1'];

const EXPLAINED_ICONS = {
  broadcast: LiveTvIcon,
  seo: MenuBookIcon,
  web: LanguageIcon,
  build: BuildIcon,
};

const TNO_ECOSYSTEM_AXES = [
  {
    title: 'Streaming',
    Icon: LiveTvIcon,
    text: 'El impacto del vivo y la conexión humana.',
    accent: '#e53935',
    soft: 'rgba(229, 57, 53, 0.08)',
  },
  {
    title: 'On-Demand',
    Icon: VideoLibraryIcon,
    text: 'El catálogo original disponible en todo momento.',
    accent: '#5e35b1',
    soft: 'rgba(94, 53, 177, 0.08)',
  },
  {
    title: 'Digest',
    Icon: MenuBookIcon,
    text: 'La inteligencia editorial para quienes buscan profundidad.',
    accent: '#00695c',
    soft: 'rgba(0, 105, 92, 0.08)',
  },
];

function InvestmentTable({ investmentSummary, brand }) {
  const theme = useTheme();
  const primary = brand?.primary || theme.palette.primary.main;
  const summaryItems = Array.isArray(investmentSummary.items) ? investmentSummary.items : [];
  const showMoney = summaryItems.some(
    (row) => row?.investment && String(row.investment).toLowerCase() !== 'incluido'
  );

  if (summaryItems.length === 0) return null;

  return (
    <Container id="inversion">
      <SectionHeader
        overline="Resumen"
        title="Inversión mensual"
        description="Desglose por línea. Los importes no incluyen IVA."
        brand={brand}
      />
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          maxWidth: 960,
          mx: 'auto',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {summaryItems.map((row, i) => (
            <Box
              key={i}
              sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                {row.product}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: showMoney ? 1 : 0, lineHeight: 1.6 }}>
                {row.detail}
              </Typography>
              {showMoney && (
                <Typography variant="h6" sx={{ fontWeight: 800, color: primary }}>
                  {row.investment}
                </Typography>
              )}
            </Box>
          ))}
          {investmentSummary.totalLabel && (
            <Box sx={{ p: 2, bgcolor: primary, color: 'common.white' }}>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {investmentSummary.totalLabel}
              </Typography>
              {showMoney && (
                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                  {investmentSummary.totalValue}
                </Typography>
              )}
            </Box>
          )}
        </Box>
        <Table sx={{ display: { xs: 'none', md: 'table' } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: primary }}>
              <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Producto / servicio</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Detalle</TableCell>
              {showMoney && (
                <TableCell align="right" sx={{ color: 'white', fontWeight: 700, py: 2 }}>
                  Inversión
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {summaryItems.map((row, i) => (
              <TableRow
                key={i}
                hover
                sx={{
                  '&:nth-of-type(even)': { bgcolor: 'rgba(0,0,0,0.02)' },
                }}
              >
                <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>{row.product}</TableCell>
                <TableCell sx={{ lineHeight: 1.65, color: 'text.secondary' }}>{row.detail}</TableCell>
                {showMoney && (
                  <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap', color: primary }}>
                    {row.investment}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {investmentSummary.totalLabel && (
              <TableRow sx={{ bgcolor: primary }}>
                <TableCell colSpan={showMoney ? 2 : 1} sx={{ fontWeight: 700, color: 'common.white', py: 2 }}>
                  {investmentSummary.totalLabel}
                </TableCell>
                {showMoney && (
                  <TableCell align="right" sx={{ fontWeight: 900, color: 'common.white', fontSize: '1.15rem', py: 2 }}>
                    {investmentSummary.totalValue}
                  </TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {showMoney && <PriceVatDisclaimer sx={{ maxWidth: 960 }} />}
    </Container>
  );
}

InvestmentTable.propTypes = {
  investmentSummary: PropTypes.object.isRequired,
  brand: PropTypes.object,
};

const TnoConStyleCommercialProposalView = ({ content }) => {
  const hero = content.hero || {};
  const ui = content.ui || {};
  const brand = mergeBrand(ui);
  const enhanced = ui.enhanced !== false;
  const tnoEcosystem = content.tnoEcosystem;
  const productsExplained = content.productsExplained || {};
  const explainedItems = Array.isArray(productsExplained.items) ? productsExplained.items : [];
  const metricsSection = content.metricsSection || {};
  const metricsRaw = Array.isArray(content.metrics) ? content.metrics : [];
  const valueAdd = content.valueAdd || {};
  const valueItems = Array.isArray(valueAdd.items) ? valueAdd.items : [];
  const investmentSummary = content.investmentSummary || {};
  const areYouReady = content.areYouReady || {};
  const planIntegralStrip = content.planIntegralStrip || {};
  const plansSection = content.plansSection || {};
  const products = Array.isArray(content.products) ? content.products : [];
  const plansLayout = ui.plansLayout || 'table';
  const showHorizontalPlans = plansLayout === 'horizontal' && products.length > 0;
  const whatsappRaw = areYouReady.whatsappNumber || '584123964576';
  const whatsappNumber = String(whatsappRaw).replace(/\D/g, '');
  const showProgramNav = ui.showProgramIndex !== false && explainedItems.length >= 4;
  const programsSectionId = 'programas';

  const getPillarGridMd = (count, columnsPerRow) => {
    if (columnsPerRow === 1) return 12;
    if (columnsPerRow === 2) return 6;
    if (columnsPerRow === 3) return 4;
    if (count >= 6) return 6;
    if (count >= 4) return 4;
    return 6;
  };
  const pillarGridMd = getPillarGridMd(explainedItems.length, productsExplained.columnsPerRow);

  const heroGradient = hero.gradient || brand.heroGradient;
  const ctaSecondary = brand.ctaSecondary || '#FFCC00';
  const ctaSecondaryText = brand.ctaSecondaryText || '#1a237e';

  return (
    <Box sx={{ bgcolor: 'background.default', scrollBehavior: 'smooth' }}>
      <Box
        minHeight={{ xs: 'auto', md: 'min(85vh, 720px)' }}
        position="relative"
        sx={{
          background: heroGradient,
          color: 'common.white',
          overflow: 'hidden',
          '&:before': {
            content: '" "',
            position: 'absolute',
            top: -120,
            right: -60,
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${brand.accent}66, transparent 70%)`,
          },
          '&:after': {
            content: '" "',
            position: 'absolute',
            bottom: -80,
            left: -40,
            width: 280,
            height: 280,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.08), transparent)',
          },
        }}
      >
        <Container>
          <Box py={{ xs: 6, md: 10 }} position="relative" zIndex={2}>
            <Chip
              label={hero.overline || 'Propuesta de alianza'}
              sx={{
                mb: 3,
                color: 'common.white',
                bgcolor: 'rgba(255,255,255,0.14)',
                fontWeight: 600,
                backdropFilter: 'blur(6px)',
              }}
            />
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap={{ xs: 2, sm: 3 }}
              mb={3}
            >
              {hero.image && (
                <Box
                  component="img"
                  src={hero.image}
                  alt={hero.imageAlt || 'Logo patrocinador'}
                  sx={{
                    height: { xs: 44, sm: 52, md: 56 },
                    maxWidth: { xs: 140, sm: 180 },
                    display: 'block',
                    objectFit: 'contain',
                    bgcolor: hero.imageBg || 'transparent',
                    borderRadius: hero.imageBg ? 1 : 0,
                    px: hero.imageBg ? 1 : 0,
                  }}
                />
              )}
              {(hero.showTnoLogo || !hero.image) && (
                <Box
                  component="figure"
                  aria-label="TNO Network"
                  sx={{
                    m: 0,
                    width: { xs: 200, sm: 260, md: 300 },
                    height: { xs: 56, sm: 72, md: 88 },
                    backgroundImage: 'url(/images/logos/logo_white.PNG)',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'contain',
                    backgroundPosition: 'left center',
                  }}
                />
              )}
            </Box>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                lineHeight: 1.12,
                fontSize: { xs: '1.85rem', sm: '2.35rem', md: '2.75rem' },
                maxWidth: 900,
              }}
              gutterBottom
            >
              {hero.title}
            </Typography>
            {hero.subtitle && (
              <Typography
                variant="h6"
                sx={{
                  maxWidth: 720,
                  opacity: 0.92,
                  fontWeight: 400,
                  lineHeight: 1.55,
                  fontSize: { xs: '1rem', md: '1.15rem' },
                }}
                paragraph
              >
                {hero.subtitle}
              </Typography>
            )}
            {(hero.analysisTitle || hero.analysisText) && (
              <Box
                sx={{
                  maxWidth: 640,
                  mt: 3,
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.12)',
                }}
              >
                {hero.analysisTitle && (
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, opacity: 0.9 }}>
                    {hero.analysisTitle}
                  </Typography>
                )}
                {hero.analysisText && (
                  <Typography variant="body1" sx={{ lineHeight: 1.8, opacity: 0.95, fontSize: '0.95rem' }}>
                    {hero.analysisText}
                  </Typography>
                )}
              </Box>
            )}
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} flexWrap="wrap" gap={2} mt={4}>
              {areYouReady.ctaText && (
                <Button
                  component="a"
                  href={`https://wa.me/${whatsappNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  size="large"
                  sx={{
                    fontWeight: 700,
                    px: 3,
                    borderRadius: 2,
                    textDecoration: 'none',
                    bgcolor: ctaSecondary,
                    color: ctaSecondaryText,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                    '&:hover': { bgcolor: ctaSecondary, filter: 'brightness(0.95)', boxShadow: '0 6px 24px rgba(0,0,0,0.25)' },
                  }}
                >
                  {areYouReady.ctaText}
                </Button>
              )}
              {areYouReady.contactEmail && (
                <Button
                  component="a"
                  href={`mailto:${areYouReady.contactEmail}`}
                  variant="outlined"
                  size="large"
                  sx={{
                    fontWeight: 600,
                    px: 3,
                    borderRadius: 2,
                    textDecoration: 'none',
                    color: 'common.white',
                    borderColor: 'rgba(255,255,255,0.5)',
                    '&:hover': { borderColor: 'common.white', bgcolor: 'rgba(255,255,255,0.08)' },
                  }}
                >
                  Email comercial
                </Button>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {explainedItems.length > 0 && (
        <>
          {tnoEcosystem ? (
            <TnoEcosystemSection section={tnoEcosystem} />
          ) : (
            <Container>
              <Box py={{ xs: 5, md: 8 }}>
                <SectionHeader
                  title="El ecosistema TNO"
                  description="Somos un ecosistema de comunicación integral que no se detiene. Unimos la inmediatez con la profundidad a través de tres ejes:"
                  brand={brand}
                />
                <Grid container spacing={3}>
                  {TNO_ECOSYSTEM_AXES.map((item) => {
                    const AxisIcon = item.Icon;
                    return (
                      <Grid item xs={12} md={4} key={item.title}>
                        <Card
                          elevation={0}
                          sx={{
                            p: 3,
                            height: '100%',
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'rgba(0,0,0,0.08)',
                            borderTop: `4px solid ${item.accent}`,
                            bgcolor: item.soft,
                          }}
                        >
                          <Box
                            width={48}
                            height={48}
                            borderRadius={2}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            mb={2}
                            sx={{ bgcolor: item.accent, color: 'common.white' }}
                          >
                            <AxisIcon />
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 800 }} gutterBottom>
                            {item.title}
                          </Typography>
                          <Typography color="textSecondary" sx={{ lineHeight: 1.7 }}>
                            {item.text}
                          </Typography>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Container>
          )}

          <Box sx={{ bgcolor: 'grey.50' }} id={programsSectionId}>
            <Container>
              <Box py={{ xs: 5, md: 8 }}>
                <Grid container spacing={4}>
                  {showProgramNav && (
                    <Grid item lg={3}>
                      <ProgramIndexNav items={explainedItems} brand={brand} sectionId={programsSectionId} />
                    </Grid>
                  )}
                  <Grid item xs={12} lg={showProgramNav ? 9 : 12}>
                    <SectionHeader
                      overline={productsExplained.overline}
                      title={productsExplained.title}
                      description={productsExplained.description}
                      brand={brand}
                    />
                    <Grid container spacing={3}>
                      {explainedItems.map((item, i) => {
                        const IconComp = EXPLAINED_ICONS[item.icon] || LiveTvIcon;
                        const anchorId = item.id ? `${programsSectionId}-${item.id}` : `${programsSectionId}-${i}`;
                        return (
                          <Grid item xs={12} md={pillarGridMd} key={item.id || i}>
                            <Box id={anchorId} sx={{ scrollMarginTop: 96 }}>
                              {enhanced ? (
                                <ProgramCard item={item} index={i} brand={brand} IconComp={IconComp} />
                              ) : (
                                <Card variant="outlined" sx={{ p: 3, height: '100%' }}>
                                  <Typography variant="h5">{item.title}</Typography>
                                  <Typography color="textSecondary">{item.summary}</Typography>
                                </Card>
                              )}
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Container>
          </Box>
        </>
      )}

      {showHorizontalPlans && (
        <HorizontalPlansSection plansSection={plansSection} products={products} brand={brand} />
      )}

      {valueItems.length > 0 && (
        <Box sx={{ bgcolor: 'common.white' }}>
          <Container>
            <Box py={{ xs: 5, md: 8 }}>
              <SectionHeader overline={valueAdd.overline} title={valueAdd.title} brand={brand} />
              <Grid container spacing={2.5}>
                {valueItems.map((item, i) => (
                  <Grid item xs={12} md={valueItems.length >= 4 ? 3 : 4} key={i}>
                    {enhanced ? (
                      <ValuePillarCard item={item} index={i} brand={brand} />
                    ) : (
                      <Card variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {item.description}
                        </Typography>
                      </Card>
                    )}
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Container>
        </Box>
      )}

      {metricsRaw.length > 0 && (
        <Box sx={{ bgcolor: 'grey.50' }}>
          <Container>
            <Box py={{ xs: 5, md: 8 }}>
              <SectionHeader
                overline={metricsSection.overline}
                title={metricsSection.title}
                description={metricsSection.description}
                brand={brand}
              />
              <Grid container spacing={2}>
                {metricsRaw.map((m, i) => {
                  const accent = m.accent || METRIC_ACCENTS[i % METRIC_ACCENTS.length];
                  const displayValue = m.value != null ? `${m.value}${m.suffix || ''}` : m.title;
                  return (
                    <Grid item xs={12} sm={6} md={3} key={i}>
                      <Card
                        elevation={0}
                        sx={{
                          p: 2.5,
                          height: '100%',
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'rgba(0,0,0,0.08)',
                          borderTop: `4px solid ${accent}`,
                        }}
                      >
                        <Typography variant="h4" sx={{ fontWeight: 900, color: accent }} gutterBottom>
                          {displayValue}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.65 }}>
                          {m.label}
                        </Typography>
                        {m.subtitle && (
                          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                            {m.subtitle}
                          </Typography>
                        )}
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              {metricsSection.footerLine && (
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 4, maxWidth: 720, color: 'text.secondary' }}>
                  {metricsSection.footerLine}
                </Typography>
              )}
            </Box>
          </Container>
        </Box>
      )}

      {(planIntegralStrip.title || planIntegralStrip.description) && (
        <PlanHighlightStrip
          strip={planIntegralStrip}
          brand={brand}
          totalValue={investmentSummary.totalValue}
        />
      )}

      {Array.isArray(content.extraSections) &&
        content.extraSections.map((section, si) => (
          <Box key={section.id || si} sx={{ bgcolor: si % 2 === 0 ? 'grey.50' : 'common.white' }}>
            <Container>
              <Box py={{ xs: 5, md: 8 }}>
                <SectionHeader
                  overline={section.overline}
                  title={section.title}
                  description={section.description}
                  brand={brand}
                  id={section.id}
                />
                {Array.isArray(section.groups) && (
                  <Grid container spacing={3}>
                    {section.groups.map((group, gi) => (
                      <Grid item xs={12} md={6} key={gi}>
                        {enhanced ? (
                          <ExtraGroupCard group={group} brand={brand} index={gi} />
                        ) : (
                          <Card variant="outlined" sx={{ p: 3, height: '100%' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              {group.label}
                            </Typography>
                            <Box component="ul" sx={{ pl: 2 }}>
                              {group.bullets?.map((b, bi) => (
                                <li key={bi}>
                                  <Typography variant="body2" color="textSecondary">
                                    {b}
                                  </Typography>
                                </li>
                              ))}
                            </Box>
                          </Card>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                )}
                {Array.isArray(section.items) && (
                  <Grid container spacing={3}>
                    {section.items.map((item, ii) => (
                      <Grid item xs={12} md={section.items.length >= 4 ? 6 : 4} key={ii}>
                        <Card elevation={0} sx={{ p: 3, height: '100%', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="h6" sx={{ fontWeight: 700 }} gutterBottom>
                            {item.title}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.75 }}>
                            {item.description}
                          </Typography>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Container>
          </Box>
        ))}

      {!showHorizontalPlans && (
        <Box py={{ xs: 5, md: 7 }}>
          <InvestmentTable investmentSummary={investmentSummary} brand={brand} />
        </Box>
      )}

      <CtaBand areYouReady={areYouReady} brand={brand} whatsappNumber={whatsappNumber} />

      <Box component="footer" sx={{ bgcolor: 'grey.900', color: 'grey.100' }}>
        <Container>
          <Box py={{ xs: 4, md: 5 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
              TNO Network
            </Typography>
            {areYouReady.footerText && (
              <Typography variant="body2" sx={{ opacity: 0.85, lineHeight: 1.7 }}>
                {areYouReady.footerText}
              </Typography>
            )}
            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.15)' }} />
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              <Link href="https://www.tnonetwork.com" target="_blank" rel="noreferrer" color="inherit" underline="hover">
                tnonetwork.com
              </Link>
              {' · '}
              <Link href="https://www.instagram.com/tnonetwork/" target="_blank" rel="noreferrer" color="inherit" underline="hover">
                @tnonetwork
              </Link>
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

TnoConStyleCommercialProposalView.propTypes = {
  content: PropTypes.object.isRequired,
};

export default TnoConStyleCommercialProposalView;
